import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom, throwError } from 'rxjs';
import { timeout } from 'rxjs/operators';

import { InbetriebnahmeService } from '../inbetriebnahme.service';
import { OfflineDbService } from './offline-db.service';
import { NetworkStatusService, TimeoutFehler } from './network-status.service';

import {
  findeSpalte,
  istLokaleId,
  normalizePruefpunktErgebnis,
  PruefpunktBewertung,
  PruefpunktErgebnis,
  PruefpunktStatus
} from '../../Models/messstelle.model';
import { SpsSignal } from '../../Models/Sps';
import {
  KonfliktEntscheidung,
  OfflineDetailEintrag,
  OfflineQrZuordnungEintrag,
  SyncAenderung,
  SyncErfolg,
  SyncErgebnis,
  SyncKonflikt,
  SyncKonfliktEintrag,
  SyncQueueEintrag,
  SyncStatus
} from '../../Models/offline.model';

/** Warum ein Abgleich angestoßen wurde – nur für die Nachvollziehbarkeit im Log. */
export type SyncAnlass =
  | 'start'
  | 'online'
  | 'sichtbar'
  | 'geoeffnet'
  | 'aenderung'
  | 'manuell';

/** Eine Meldung für den Benutzer, wenn ein automatischer Abgleich nicht glatt lief. */
export interface SyncBenachrichtigung {
  art: 'konflikt' | 'fehler' | 'keine_verbindung';
  text: string;
}

/**
 * Bringt offline erfasste Änderungen zum Server.
 *
 * Der Ablauf ist bewusst schlicht: Warteschlange in Erfassungsreihenfolge
 * abschicken, jede Antwort einzeln auswerten, und was nicht angekommen ist,
 * bleibt liegen. Es gibt keinen Zustand, in dem eine Änderung aus der
 * Warteschlange verschwindet, ohne dass sie entweder beim Server ist oder in
 * der Konfliktliste steht.
 *
 * Kein Polling im Sekundentakt. Angestoßen wird bei:
 * Anwendungsstart, `online`-Ereignis des Browsers, Öffnen einer offline
 * gespeicherten Inbetriebnahme, nach einer Änderung (wenn eine Verbindung
 * plausibel ist) und über den Knopf „Jetzt synchronisieren".
 *
 * Seit der projektweiten Offline-Nutzung trägt dieselbe Warteschlange auch
 * SPS-Prüfstatus und QR-Zuordnungen – mit demselben Journal auf dem Server.
 */
@Injectable({ providedIn: 'root' })
export class OfflineSyncService {

  /**
   * Höchstens so viele Änderungen in einem Anlauf. Muss zu
   * `MaxSyncAenderungen` im Controller passen und hält eine einzelne Anfrage
   * kurz genug, um eine dünne Leitung zu überleben.
   */
  private static readonly STAPELGROESSE = 100;

  /** Zeitgrenze für einen Stapel. Danach gilt die Leitung als tot. */
  private static readonly TIMEOUT_MS = 30_000;

  private readonly status$ = new BehaviorSubject<SyncStatus>({
    zustand: 'online',
    ausstehend: 0,
    konflikte: 0,
    fehlgeschlagen: 0,
    meldung: 'Alles synchronisiert'
  });

  /**
   * Meldungen, die der Benutzer sehen soll – ein automatischer Abgleich, der
   * in Konflikt, Fehler oder ohne Verbindung endet, darf nicht lautlos bleiben.
   */
  private readonly benachrichtigungen$ = new Subject<SyncBenachrichtigung>();

  /** Verhindert, dass zwei Abgleiche gleichzeitig dieselbe Warteschlange abarbeiten. */
  private laeuft = false;

  /**
   * Ein Abgleich, der während eines laufenden angefordert wurde. Wird danach
   * einmal nachgeholt – so geht ein Anstoß nicht verloren, ohne dass sich
   * Aufrufe stapeln.
   */
  private nachholen = false;

  constructor(
    private api: InbetriebnahmeService,
    private db: OfflineDbService,
    private network: NetworkStatusService
  ) {
    // Kommt das Netz zurück, sofort versuchen: das ist der Moment, in dem der
    // Monteur die Halle verlässt und wieder Empfang hat.
    this.network.online$.subscribe(online => {
      if (online) {
        void this.synchronisiere('online');
      } else {
        void this.aktualisiereStatus();
      }
    });

    /*
     * Zusätzlich beim Zurückkehren zur Anwendung.
     *
     * Auf einem Tablet ist das der häufigste Fall überhaupt: der Bildschirm
     * war aus, das Gerät fährt aus dem Funkloch, und das `online`-Ereignis
     * des Browsers kommt entweder gar nicht oder während die Seite im
     * Hintergrund schlief.
     */
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          void this.synchronisiere('sichtbar');
        }
      });
    }

    void this.aktualisiereStatus();
  }

  get syncStatus$(): Observable<SyncStatus> {
    return this.status$.asObservable();
  }

  get aktuellerStatus(): SyncStatus {
    return this.status$.value;
  }

  get benachrichtigung$(): Observable<SyncBenachrichtigung> {
    return this.benachrichtigungen$.asObservable();
  }

  get laeuftGerade(): boolean {
    return this.laeuft;
  }

  // ===================================================================
  // WARTESCHLANGE FÜLLEN
  // ===================================================================

  /**
   * Legt eine Änderung in die Warteschlange.
   *
   * Wird immer ZUSAMMEN mit der lokalen Datenänderung aufgerufen, innerhalb
   * derselben Dexie-Transaktion – siehe `InbetriebnahmeDataService`. Getrennt
   * gespeichert könnte die Oberfläche eine Prüfung zeigen, die nie zum Server
   * geht.
   */
  async stelleEin(eintrag: SyncQueueEintrag): Promise<void> {
    await this.db.db.syncQueue.put(eintrag);
    await this.aktualisiereStatus();
  }

  // ===================================================================
  // ABGLEICH
  // ===================================================================

  /**
   * Schickt die Warteschlange zum Server.
   *
   * Tut nichts, wenn nichts aussteht oder ein Versuch gerade sinnlos wäre
   * (Browser offline, oder kurz zuvor ist eine Anfrage im Netzwerkfehler
   * geendet und die Wartezeit läuft noch).
   */
  async synchronisiere(anlass: SyncAnlass = 'manuell'): Promise<SyncStatus> {
    if (this.laeuft) {
      this.nachholen = true;
      return this.status$.value;
    }

    if (!(await this.db.istVerfuegbar())) {
      return this.status$.value;
    }

    const offen = await this.offeneEintraege();

    if (offen.length === 0) {
      return this.aktualisiereStatus();
    }

    /*
     * Die Wartezeit nach einem Netzwerkfehler gilt für Hintergrundversuche.
     * Übergangen wird sie, wenn ein starkes Signal vorliegt, dass sich die
     * Lage geändert hat: der Benutzer drückt den Knopf, der Browser meldet
     * wieder Netz, oder die Anwendung kommt in den Vordergrund.
     */
    const sofortVersuchen =
      anlass === 'manuell'
      || anlass === 'online'
      || anlass === 'sichtbar';

    if (!sofortVersuchen && !this.network.lohntVersuch()) {
      return this.aktualisiereStatus();
    }

    /*
     * Galt der Server zuletzt als nicht erreichbar, erst anklopfen. Das
     * `online`-Ereignis des Browsers sagt nur, dass eine Schnittstelle aktiv
     * ist – nicht, dass das Backend antwortet. Ein ganzer Stapel müsste
     * sonst erst scheitern, um das festzustellen.
     */
    if (!this.network.backendGiltAlsErreichbar || anlass === 'manuell') {
      const erreichbar = await this.network.pruefeErreichbarkeit();

      if (!erreichbar) {
        if (anlass === 'manuell') {
          this.benachrichtigungen$.next({
            art: 'keine_verbindung',
            text: 'Der Server ist nicht erreichbar. Die Änderungen bleiben gespeichert und werden später übertragen.'
          });
        }

        return this.aktualisiereStatus();
      }
    }

    const vorher = this.status$.value;

    this.laeuft = true;
    this.setzeStatus({ zustand: 'laeuft', meldung: 'Synchronisierung läuft …' });

    let abgebrochen = false;

    try {
      for (let i = 0; i < offen.length; i += OfflineSyncService.STAPELGROESSE) {
        const stapel = offen.slice(i, i + OfflineSyncService.STAPELGROESSE);

        abgebrochen = await this.sendeStapel(stapel);

        if (abgebrochen) {
          // Netzwerkfehler: der Rest bleibt liegen und wird beim nächsten
          // Anlauf erneut versucht. Nichts geht verloren.
          break;
        }
      }
    } finally {
      this.laeuft = false;
    }

    const status = await this.aktualisiereStatus();

    this.melde(vorher, status, abgebrochen);

    if (this.nachholen) {
      this.nachholen = false;
      return this.synchronisiere(anlass);
    }

    return status;
  }

  /** Erzählt dem Benutzer, wenn der Abgleich nicht glatt durchlief. */
  private melde(vorher: SyncStatus, nachher: SyncStatus, abgebrochen: boolean): void {
    if (nachher.konflikte > vorher.konflikte) {
      const neue = nachher.konflikte - vorher.konflikte;

      this.benachrichtigungen$.next({
        art: 'konflikt',
        text: neue === 1
          ? 'Eine Änderung braucht eine Entscheidung: sie wurde auch am Server geändert.'
          : `${neue} Änderungen brauchen eine Entscheidung: sie wurden auch am Server geändert.`
      });

      return;
    }

    if (nachher.fehlgeschlagen > vorher.fehlgeschlagen) {
      this.benachrichtigungen$.next({
        art: 'fehler',
        text: 'Der Server hat eine Änderung abgelehnt. Bitte in der Synchronisierungsanzeige prüfen.'
      });

      return;
    }

    if (abgebrochen && nachher.ausstehend > 0 && nachher.zustand === 'fehler') {
      this.benachrichtigungen$.next({
        art: 'fehler',
        text: nachher.letzterFehlertext ?? 'Die Synchronisierung konnte nicht abgeschlossen werden.'
      });
    }
  }

  /**
   * Schickt einen Stapel und wertet die Antwort aus.
   * Gibt `true` zurück, wenn wegen eines Netzwerkfehlers abgebrochen wurde.
   */
  private async sendeStapel(stapel: SyncQueueEintrag[]): Promise<boolean> {
    const changes: SyncAenderung[] = stapel.map(e => ({
      clientId: e.id,
      typ: e.typ,

      // Eine lokal vergebene Kennung ist keine Server-Id – sie geht als
      // clientEntityId mit, damit der Server den inzwischen angelegten
      // Datensatz darüber wiederfindet.
      entityId: typeof e.entityId === 'number' ? e.entityId : null,
      clientEntityId: e.clientEntityId ?? null,

      messstellenListeId: e.messstellenListeId,
      messstelleId: e.messstelleId ?? null,
      projektId: e.projektId ?? null,
      spsSignalId: e.spsSignalId ?? null,

      // Hinweis für Punkte, die offline aus einer Kategorie entstanden sind.
      vorlageId: e.vorlageId ?? null,

      performedAtUtc: e.performedAtUtc,
      baseVersion: e.baseVersion ?? null,
      basisWerte: e.basisWerte ?? null,
      payload: e.payload
    }));

    let ergebnis: SyncErgebnis;

    try {
      ergebnis = await firstValueFrom(
        this.api.sync(changes).pipe(
          timeout({
            each: OfflineSyncService.TIMEOUT_MS,
            // Muss ein Observable liefern - ein synchrones throw im Fabrikaufruf
            // waere ein anderer Fehlerweg und ginge an istNetzwerkfehler vorbei.
            with: () => throwError(() => new TimeoutFehler(OfflineSyncService.TIMEOUT_MS))
          })
        )
      );

      this.network.meldeErfolg();
    } catch (fehler: any) {
      if (NetworkStatusService.istAuthFehler(fehler)) {
        /*
         * Die Anmeldung ist abgelaufen. Die Warteschlange bleibt UNANGETASTET
         * – nach dem nächsten Login geht es weiter. Ein Datenverlust wegen
         * eines abgelaufenen Tokens wäre der schlimmste denkbare Fehler
         * dieser Funktion.
         */
        this.setzeStatus({
          zustand: 'fehler',
          meldung: 'Anmeldung abgelaufen – nach dem nächsten Login wird weiter synchronisiert.'
        });

        return true;
      }

      if (NetworkStatusService.istNetzwerkfehler(fehler)) {
        this.network.meldeNetzwerkfehler();

        // Nur den Zähler hochsetzen. Der Eintrag selbst bleibt unverändert –
        // insbesondere seine Kennung, die die Idempotenz trägt.
        await this.db.db.transaction('rw', this.db.db.syncQueue, async () => {
          for (const e of stapel) {
            await this.db.db.syncQueue.update(e.id, {
              retryCount: (e.retryCount ?? 0) + 1,
              letzterFehler: 'Keine Verbindung zum Server.'
            });
          }
        });

        return true;
      }

      // Alles andere (400 auf Stapelebene, 500) ist eine echte Antwort. Der
      // Stapel bleibt liegen, damit nichts stillschweigend verschwindet.
      await this.db.db.transaction('rw', this.db.db.syncQueue, async () => {
        for (const e of stapel) {
          await this.db.db.syncQueue.update(e.id, {
            retryCount: (e.retryCount ?? 0) + 1,
            letzterFehler: fehler?.error?.message ?? 'Der Server hat die Übertragung abgelehnt.'
          });
        }
      });

      return true;
    }

    await this.verarbeiteAntwort(stapel, ergebnis);

    return false;
  }

  /**
   * Wertet die Antwort aus: Erfolge räumen die Warteschlange, Konflikte
   * wandern in die Konfliktliste, endgültige Fehler bleiben sichtbar liegen.
   */
  private async verarbeiteAntwort(
    stapel: SyncQueueEintrag[],
    ergebnis: SyncErgebnis
  ): Promise<void> {
    const db = this.db.db;
    const nachId = new Map(stapel.map(e => [e.id, e]));
    const listenIds = new Set(stapel.map(e => e.messstellenListeId).filter(id => id > 0));
    const projektIds = new Set(
      stapel.map(e => e.projektId).filter((id): id is number => typeof id === 'number')
    );

    await db.transaction(
      'rw',
      [
        db.syncQueue, db.konflikte, db.details, db.messstellen, db.listen,
        db.projekte, db.spsSignale, db.qrZuordnungen
      ],
      async () => {
        // ── Erfolge ──
        for (const erfolg of ergebnis.successful ?? []) {
          const eintrag = nachId.get(erfolg.clientId);

          await db.syncQueue.delete(erfolg.clientId);

          if (!eintrag) {
            continue;
          }

          if (eintrag.typ === 'SPS_PRUEFSTATUS') {
            await this.uebernehmeSpsErfolg(eintrag, erfolg);
            continue;
          }

          if (eintrag.typ === 'QR_ZUORDNEN') {
            await this.uebernehmeQrErfolg(eintrag, erfolg);
            continue;
          }

          /*
           * Ein offline angelegter Prüfpunkt hat jetzt eine echte Id. Die
           * lokale Kennung muss überall nachgezogen werden – im gespeicherten
           * Detaildatensatz und in allen noch wartenden Änderungen, die sich
           * auf ihn beziehen.
           */
          if (erfolg.serverId != null && eintrag.clientEntityId) {
            await this.ersetzeLokaleKennung(
              eintrag.messstelleId ?? null,
              eintrag.clientEntityId,
              erfolg.serverId
            );
          }

          if (eintrag.messstelleId != null) {
            await this.uebernehmeVersion(
              eintrag,
              erfolg.serverId ?? erfolg.entityId ?? null,
              erfolg.version ?? null
            );
          }
        }

        // ── Konflikte ──
        for (const konflikt of ergebnis.conflicts ?? []) {
          const eintrag = nachId.get(konflikt.clientId);

          if (!eintrag) {
            continue;
          }

          /*
           * Aus der Warteschlange heraus in die Konfliktliste. Der
           * ursprüngliche Eintrag wandert mit – wenn der Benutzer sich für
           * seine Fassung entscheidet, wird genau er erneut eingestellt.
           *
           * Wichtig: die Kennung bleibt dieselbe. Der Server hat die Änderung
           * ja NICHT angewendet, also ist sie noch nicht im Journal – ein
           * erneutes Senden unter derselben Kennung ist korrekt.
           */
          await db.konflikte.put(await this.alsKonfliktEintrag(eintrag, konflikt));
          await db.syncQueue.delete(konflikt.clientId);
        }

        // ── Fehler ──
        for (const fehler of ergebnis.failed ?? []) {
          if (fehler.wiederholbar) {
            await db.syncQueue.update(fehler.clientId, {
              retryCount: (nachId.get(fehler.clientId)?.retryCount ?? 0) + 1,
              letzterFehler: fehler.message
            });
            continue;
          }

          /*
           * Endgültig abgelehnt – erneutes Senden ändert nichts. Der Eintrag
           * bleibt trotzdem stehen, markiert und sichtbar: der Benutzer soll
           * erfahren, was nicht angekommen ist, statt es stillschweigend zu
           * verlieren.
           */
          console.error(
            'Der Server hat eine Offline-Änderung endgültig abgelehnt.',
            {
              typ: fehler.typ,
              statusCode: fehler.statusCode,
              meldung: fehler.message,
              entityId: fehler.entityId,
              aenderung: nachId.get(fehler.clientId)
            }
          );

          await db.syncQueue.update(fehler.clientId, {
            endgueltigFehlgeschlagen: true,
            letzterFehler: `${fehler.message} (${fehler.statusCode})`
          });
        }

        // ── Zeitstempel der Listen und Projekte ──
        const jetzt = new Date().toISOString();

        for (const listeId of listenIds) {
          if (await db.listen.get(listeId)) {
            await db.listen.update(listeId, { lastSyncAtUtc: jetzt });
          }
        }

        for (const projektId of projektIds) {
          if (await db.projekte.get(projektId)) {
            await db.projekte.update(projektId, { lastSyncAtUtc: jetzt });
          }
        }
      }
    );
  }

  /** Baut aus Serverantwort und Warteschlangeneintrag den lokalen Konflikt – mit Kontext für die Anzeige. */
  private async alsKonfliktEintrag(
    eintrag: SyncQueueEintrag,
    konflikt: SyncKonflikt
  ): Promise<SyncKonfliktEintrag> {
    const db = this.db.db;

    const projektId = konflikt.projektId ?? eintrag.projektId ?? null;
    const liste = eintrag.messstellenListeId > 0 ? await db.listen.get(eintrag.messstellenListeId) : undefined;
    const projekt = projektId != null ? await db.projekte.get(projektId) : undefined;

    return {
      id: konflikt.clientId,
      typ: konflikt.typ,
      messstellenListeId: eintrag.messstellenListeId,
      messstelleId: eintrag.messstelleId ?? null,
      entityId: konflikt.entityId ?? null,
      projektId,
      spsSignalId: konflikt.spsSignalId ?? eintrag.spsSignalId ?? null,
      projektName: projekt?.name ?? liste?.projektName ?? null,
      listeName: liste?.name ?? null,
      art: konflikt.art ?? 'FELD',
      bezeichnung: konflikt.bezeichnung,
      feld: konflikt.feld,
      serverText: konflikt.serverText,
      lokalText: konflikt.lokalText,
      serverVersion: konflikt.serverVersion,
      serverBenutzer: konflikt.serverBenutzer ?? null,
      serverZeitpunktUtc: konflikt.serverZeitpunktUtc ?? null,
      felder: konflikt.felder ?? [],
      serverWerte: konflikt.serverWerte ?? null,
      qr: konflikt.qr ?? null,
      erkanntAmUtc: new Date().toISOString(),
      aenderung: eintrag
    };
  }

  /** SPS: der Server hat Status und Bemerkung übernommen – lokales Signal nachziehen. */
  private async uebernehmeSpsErfolg(eintrag: SyncQueueEintrag, erfolg: SyncErfolg): Promise<void> {
    if (eintrag.spsSignalId == null) {
      return;
    }

    const lokal = await this.db.db.spsSignale.get(eintrag.spsSignalId);

    if (!lokal) {
      return;
    }

    const server = erfolg.daten as SpsSignal | undefined;

    if (server && typeof server === 'object' && 'pruefstatus' in server) {
      lokal.data = {
        ...lokal.data,
        pruefstatus: server.pruefstatus,
        bemerkung: server.bemerkung ?? null,
        zuletztGeprueftAmUtc: server.zuletztGeprueftAmUtc ?? lokal.data.zuletztGeprueftAmUtc,
        zuletztGeprueftVon: server.zuletztGeprueftVon ?? lokal.data.zuletztGeprueftVon,
        version: server.version,
        geaendertSeitPruefungAmUtc: server.geaendertSeitPruefungAmUtc ?? null,
        geaendertSeitPruefungHinweis: server.geaendertSeitPruefungHinweis ?? null
      };
    } else if (erfolg.version != null) {
      lokal.data.version = erfolg.version;
    }

    // Lokale Verlaufseinträge sind jetzt bestätigt.
    for (const p of lokal.data.protokolle ?? []) {
      if ((p as any).nichtSynchronisiert) {
        delete (p as any).nichtSynchronisiert;
      }
    }

    await this.db.db.spsSignale.put(lokal);
  }

  /** QR: die Zuordnung ist am Server – lokal als bestätigt markieren. */
  private async uebernehmeQrErfolg(eintrag: SyncQueueEintrag, erfolg: SyncErfolg): Promise<void> {
    if (eintrag.messstelleId == null) {
      return;
    }

    const lokal = await this.db.db.qrZuordnungen.get(eintrag.messstelleId);

    if (!lokal) {
      return;
    }

    const daten = erfolg.daten as { token?: string } | undefined;

    const bestaetigt: OfflineQrZuordnungEintrag = {
      ...lokal,
      token: daten?.token ?? lokal.token,
      quelle: 'server',
      vorherigerToken: null
    };

    await this.db.db.qrZuordnungen.put(bestaetigt);
  }

  /**
   * Ersetzt die lokale Kennung eines offline angelegten Prüfpunkts durch die
   * vom Server vergebene Id – im gespeicherten Detaildatensatz und in allen
   * noch wartenden Änderungen.
   */
  private async ersetzeLokaleKennung(
    messstelleId: number | null,
    clientEntityId: string,
    serverId: number
  ): Promise<void> {
    const db = this.db.db;

    if (messstelleId != null) {
      const detail = await db.details.get(messstelleId);

      if (detail) {
        for (const punkt of detail.data.pruefpunkte ?? []) {
          if (punkt.clientId === clientEntityId || punkt.id === `local:${clientEntityId}`) {
            punkt.id = serverId;
            punkt.clientId = clientEntityId;
            punkt.nichtSynchronisiert = false;
          }
        }

        await db.details.put(detail);
      }
    }

    const betroffen = await db.syncQueue
      .filter(e => e.clientEntityId === clientEntityId)
      .toArray();

    for (const e of betroffen) {
      await db.syncQueue.update(e.id, { entityId: serverId });
    }
  }

  /**
   * Übernimmt den neuen Änderungsstand vom Server in den lokalen Bestand.
   *
   * Ohne das würde die nächste Offline-Änderung mit einer veralteten Basis
   * losgeschickt – und der Server meldete zu Recht einen Konflikt, obwohl der
   * Benutzer der einzige war, der geschrieben hat.
   */
  private async uebernehmeVersion(
    eintrag: SyncQueueEintrag,
    entityId: number | null,
    version: number | null
  ): Promise<void> {
    if (version == null || eintrag.messstelleId == null) {
      return;
    }

    const db = this.db.db;
    const detail = await db.details.get(eintrag.messstelleId);

    if (!detail) {
      return;
    }

    if (eintrag.typ === 'PRUEFPUNKT_ERGEBNIS') {
      for (const punkt of detail.data.pruefpunkte ?? []) {
        if (punkt.id === entityId || punkt.id === eintrag.entityId) {
          punkt.version = version;
          punkt.nichtSynchronisiert = false;
        }
      }
    } else {
      detail.data.version = version;
    }

    await db.details.put(detail);
  }

  // ===================================================================
  // KONFLIKTE AUFLÖSEN
  // ===================================================================

  /**
   * Die Serverfassung gilt – für alle strittigen Felder.
   *
   * Die Serverwerte werden in den lokalen Bestand GESCHRIEBEN, damit die
   * Anzeige vor Ort sofort stimmt und der Datensatz offline nutzbar bleibt.
   * Nichts wird still überschrieben – der Benutzer hat entschieden.
   */
  async uebernehmeServerfassung(konfliktId: string): Promise<void> {
    await this.loeseKonflikt(konfliktId, { gesamt: 'server' });
  }

  /**
   * Die eigene, offline erfasste Fassung gilt – für alle strittigen Felder.
   *
   * Die Änderung geht erneut in die Warteschlange, diesmal mit dem
   * Änderungsstand des Servers als Basis. Damit lässt der Server sie durch,
   * und zwar bewusst: der Benutzer hat die Serverfassung gesehen und sich
   * dagegen entschieden.
   */
  async uebernehmeLokaleFassung(konfliktId: string): Promise<void> {
    await this.loeseKonflikt(konfliktId, { gesamt: 'lokal' });
  }

  /**
   * Entscheidung je Feld – oder pauschal, wenn `feldweise` fehlt.
   *
   * Felder mit „server" werden lokal auf den Serverwert gesetzt UND im
   * erneut gesendeten Payload auf den Serverwert gestellt; Felder mit
   * „lokal" behalten den offline erfassten Wert. Gibt es nichts mehr zu
   * senden (alles „server"), wird nur lokal übernommen.
   *
   * QR-Konflikte haben keine „meine Fassung": dort gibt es nur die sicheren
   * Auswege (`qrZuordnungZuruecknehmen`, `qrVorhandenenErsetzen`).
   */
  async loeseKonflikt(konfliktId: string, entscheidung: KonfliktEntscheidung): Promise<void> {
    const db = this.db.db;
    const konflikt = await db.konflikte.get(konfliktId);

    if (!konflikt) {
      return;
    }

    if (konflikt.art === 'QR') {
      if (entscheidung.gesamt === 'server') {
        await this.qrZuordnungZuruecknehmen(konfliktId);
      }

      // „lokal" gibt es bei QR nicht – eine Doppelvergabe wird nie erzwungen.
      return;
    }

    const felder = konflikt.felder ?? [];
    const wahl = (schluessel: string): 'lokal' | 'server' =>
      entscheidung.feldweise?.[schluessel] ?? entscheidung.gesamt;

    const serverFelder = felder.filter(f => wahl(f.schluessel) === 'server').map(f => f.schluessel);
    const lokaleFelder = felder.filter(f => wahl(f.schluessel) === 'lokal').map(f => f.schluessel);

    // Ohne Feldauflösung zählt nur die Gesamtentscheidung.
    const allesServer =
      felder.length === 0 ? entscheidung.gesamt === 'server' : lokaleFelder.length === 0;

    await db.transaction(
      'rw',
      [db.konflikte, db.syncQueue, db.details, db.messstellen, db.listen, db.spsSignale],
      async () => {
        // 1. Serverwerte lokal übernehmen – für die Felder, die der Server gewinnt.
        await this.schreibeServerwerteLokal(
          konflikt,
          felder.length === 0 ? (allesServer ? null : []) : serverFelder
        );

        if (allesServer) {
          await db.konflikte.delete(konfliktId);
          return;
        }

        // 2. Den Rest erneut einstellen – mit Serverstand als Basis und
        //    Serverwerten für die abgegebenen Felder.
        const payload = { ...(konflikt.aenderung.payload ?? {}) };

        for (const schluessel of serverFelder) {
          this.setzePayloadFeld(konflikt, payload, schluessel);
        }

        const erneut: SyncQueueEintrag = {
          ...konflikt.aenderung,
          payload,

          // Der Server kennt die Kennung noch nicht – er hat die Änderung ja
          // abgelehnt. Sie bleibt deshalb dieselbe und trägt die Idempotenz
          // weiterhin.
          baseVersion: konflikt.serverVersion,
          basisWerte: null,
          retryCount: 0,
          letzterFehler: null,
          endgueltigFehlgeschlagen: false
        };

        await db.syncQueue.put(erneut);
        await db.konflikte.delete(konfliktId);
      }
    );

    await this.aktualisiereStatus();

    if (!allesServer) {
      await this.synchronisiere('manuell');
    }
  }

  /**
   * Setzt im Payload ein Feld auf den Serverwert. Bei den allgemeinen Daten
   * liegt das Feld unter `werte`, sonst direkt im Payload.
   */
  private setzePayloadFeld(konflikt: SyncKonfliktEintrag, payload: any, schluessel: string): void {
    const serverWert = konflikt.serverWerte?.[schluessel];

    if (konflikt.typ === 'MESSSTELLE_WERTE') {
      payload.werte = { ...(payload.werte ?? {}) };

      // Der Server trägt denselben Wert – das Feld muss gar nicht mehr mit.
      delete payload.werte[schluessel];
      return;
    }

    payload[schluessel] = serverWert ?? null;
  }

  /**
   * Schreibt Serverwerte in den lokalen Datensatz.
   *
   * `nurFelder === null` heißt: alle bekannten Serverwerte übernehmen.
   * Eine leere Liste schreibt nichts.
   */
  private async schreibeServerwerteLokal(
    konflikt: SyncKonfliktEintrag,
    nurFelder: string[] | null
  ): Promise<void> {
    const db = this.db.db;
    const serverWerte = konflikt.serverWerte ?? {};
    const felder = nurFelder ?? Object.keys(serverWerte);

    if (nurFelder !== null && nurFelder.length === 0) {
      return;
    }

    const gilt = (schluessel: string): boolean => felder.includes(schluessel);

    if (konflikt.typ === 'SPS_PRUEFSTATUS') {
      if (konflikt.spsSignalId == null) {
        return;
      }

      const signal = await db.spsSignale.get(konflikt.spsSignalId);

      if (!signal) {
        return;
      }

      if (gilt('status') && serverWerte['status'] != null) {
        signal.data.pruefstatus = serverWerte['status'];
      }

      if (gilt('bemerkung')) {
        signal.data.bemerkung = (serverWerte['bemerkung'] ?? null) || null;
      }

      if (nurFelder === null || felder.length === (konflikt.felder?.length ?? 0)) {
        signal.data.version = konflikt.serverVersion;
        signal.data.zuletztGeprueftVon = konflikt.serverBenutzer ?? signal.data.zuletztGeprueftVon;
        signal.data.zuletztGeprueftAmUtc = konflikt.serverZeitpunktUtc ?? signal.data.zuletztGeprueftAmUtc;
      }

      // Der eigene, noch nicht bestätigte Verlaufseintrag passt nicht mehr.
      signal.data.protokolle = (signal.data.protokolle ?? []).filter(p => !(p as any).nichtSynchronisiert);
      signal.data.letzteProtokolle = signal.data.protokolle.slice(0, 10);

      await db.spsSignale.put(signal);
      return;
    }

    if (konflikt.messstelleId == null) {
      return;
    }

    const detail = await db.details.get(konflikt.messstelleId);

    if (!detail) {
      return;
    }

    const allesUebernommen = nurFelder === null || lokaleFelderFehlen(konflikt, felder);

    switch (konflikt.typ) {
      case 'PRUEFPUNKT_ERGEBNIS': {
        const punkt = (detail.data.pruefpunkte ?? []).find(
          p => p.id === konflikt.entityId || p.id === konflikt.aenderung.entityId
        );

        if (!punkt) {
          break;
        }

        if (gilt('ergebnis') && serverWerte['ergebnis'] != null) {
          const ergebnis = normalizePruefpunktErgebnis(serverWerte['ergebnis']);
          punkt.ergebnis = ergebnis;
          punkt.status =
            ergebnis === PruefpunktErgebnis.Bestanden || ergebnis === PruefpunktErgebnis.NichtBestanden
              ? PruefpunktStatus.Erledigt
              : ergebnis === PruefpunktErgebnis.Irrelevant
                ? PruefpunktStatus.Irrelevant
                : PruefpunktStatus.Offen;
          punkt.bewertung =
            ergebnis === PruefpunktErgebnis.Bestanden
              ? PruefpunktBewertung.InOrdnung
              : ergebnis === PruefpunktErgebnis.NichtBestanden
                ? PruefpunktBewertung.NichtInOrdnung
                : PruefpunktBewertung.Keine;
          punkt.quittiertVon = konflikt.serverBenutzer ?? punkt.quittiertVon;
          punkt.quittiertAmUtc = konflikt.serverZeitpunktUtc ?? punkt.quittiertAmUtc;
        }

        if (gilt('bemerkung')) {
          punkt.bemerkung = (serverWerte['bemerkung'] ?? null) || null;
        }

        if (allesUebernommen) {
          punkt.version = konflikt.serverVersion;
          punkt.nichtSynchronisiert = false;
        }

        break;
      }

      case 'MESSSTELLE_PRUEFERGEBNIS':
        if (gilt('pruefergebnis') && serverWerte['pruefergebnis'] != null) {
          detail.data.pruefergebnis = serverWerte['pruefergebnis'];
        }
        break;

      case 'MESSSTELLE_SIGNALSTATUS':
        if (gilt('signalStatus') && serverWerte['signalStatus'] != null) {
          detail.data.signalStatus = serverWerte['signalStatus'];
        }
        break;

      case 'MESSSTELLE_KATEGORIE':
        if (gilt('kategorieId')) {
          detail.data.kategorieId = serverWerte['kategorieId'] ?? null;
          detail.data.kategorieName =
            konflikt.serverText === 'Keine Zuordnung' ? null : konflikt.serverText;
        }
        break;

      case 'MESSSTELLE_WERTE': {
        const kopf = await db.listen.get(detail.messstellenListeId);
        const columns = kopf?.liste.columns ?? [];
        const mapping = kopf?.liste.fieldMapping ?? {};
        const struktur = kopf?.liste.struktur?.spalten ?? [];

        for (const feld of felder) {
          if (!(feld in serverWerte)) {
            continue;
          }

          let spalte: string | null = null;

          if (struktur.length > 0) {
            const definition =
              struktur.find(s => s.id === feld)
              ?? struktur.find(s => s.name.trim().toLowerCase() === feld.trim().toLowerCase());
            spalte = definition?.name ?? null;
          } else {
            spalte = findeSpalte(feld, mapping, columns);
          }

          if (!spalte) {
            continue;
          }

          const wert = (serverWerte[feld] ?? '').toString();

          if (wert) {
            detail.data.values[spalte] = wert;
          } else {
            delete detail.data.values[spalte];
          }
        }
        break;
      }

      case 'PRUEFPUNKT_LOESCHEN':
        /*
         * Der Server hat den Punkt noch – lokal ist er weg, und seine Daten
         * sind nicht mehr da. Der Detaildatensatz wird deshalb als veraltet
         * entfernt und beim nächsten Öffnen mit Verbindung neu geladen.
         */
        await db.details.delete(konflikt.messstelleId);
        return;

      default:
        break;
    }

    if (allesUebernommen) {
      detail.data.version = konflikt.serverVersion;

      // Lokale Verlaufseinträge dieser Änderung sind nicht mehr maßgeblich.
      detail.data.pruefprotokoll = (detail.data.pruefprotokoll ?? []).filter(
        e => !(e.nichtSynchronisiert && istLokaleId(e.id))
      );
    }

    await db.details.put(detail);
    await this.spiegeleZeile(detail);

    function lokaleFelderFehlen(k: SyncKonfliktEintrag, uebernommen: string[]): boolean {
      return (k.felder ?? []).every(f => uebernommen.includes(f.schluessel));
    }
  }

  /** Hält die Zeile der Listenansicht mit den Detaildaten in Übereinstimmung. */
  private async spiegeleZeile(eintrag: OfflineDetailEintrag): Promise<void> {
    const zeile = await this.db.db.messstellen.get(eintrag.id);

    if (!zeile) {
      return;
    }

    const punkte = eintrag.data.pruefpunkte ?? [];

    zeile.data = {
      ...zeile.data,
      values: eintrag.data.values,
      kategorieId: eintrag.data.kategorieId ?? null,
      kategorieName: eintrag.data.kategorieName ?? null,
      pruefpunkteGesamt: punkte.length,
      pruefpunkteErledigt: punkte.filter(
        p => normalizePruefpunktErgebnis(p.ergebnis) !== PruefpunktErgebnis.NichtGeprueft
      ).length,
      signalStatus: eintrag.data.signalStatus,
      pruefergebnis: eintrag.data.pruefergebnis,
      version: eintrag.data.version
    };

    await this.db.db.messstellen.put(zeile);
  }

  // ── QR-Konflikte: nur sichere Auswege ──

  /**
   * Nimmt die offline erfasste QR-Zuordnung zurück. Hatte die Messstelle
   * laut Server vorher einen Code, kommt der zurück. Der Sticker bleibt
   * frei bzw. dort, wo er am Server hängt – eine Doppelvergabe entsteht nie.
   *
   * Gibt die Messstelle zurück, damit die Oberfläche direkt „anderen Code
   * scannen" anbieten kann.
   */
  async qrZuordnungZuruecknehmen(konfliktId: string): Promise<number | null> {
    const db = this.db.db;
    const konflikt = await db.konflikte.get(konfliktId);

    if (!konflikt) {
      return null;
    }

    const messstelleId = konflikt.messstelleId ?? null;

    await db.transaction('rw', db.konflikte, db.qrZuordnungen, db.syncQueue, async () => {
      if (messstelleId != null) {
        const lokal = await db.qrZuordnungen.get(messstelleId);

        if (lokal && lokal.quelle === 'offline') {
          const vorher = lokal.vorherigerToken ?? konflikt.qr?.vorhandenerToken ?? null;

          if (vorher) {
            await db.qrZuordnungen.put({
              ...lokal,
              token: vorher,
              quelle: 'server',
              vorherigerToken: null
            });
          } else {
            await db.qrZuordnungen.delete(messstelleId);
          }
        }

        // Eine eventuell noch wartende zweite QR-Änderung derselben Messstelle
        // wäre jetzt ebenfalls hinfällig.
        const wartend = await db.syncQueue
          .filter(e => e.typ === 'QR_ZUORDNEN' && e.messstelleId === messstelleId)
          .toArray();

        await db.syncQueue.bulkDelete(wartend.map(e => e.id));
      }

      await db.konflikte.delete(konfliktId);
    });

    await this.aktualisiereStatus();

    return messstelleId;
  }

  /**
   * „Messstelle hat schon einen Code": der gescannte Sticker soll den
   * vorhandenen ersetzen – derselbe Weg wie online „QR-Code ersetzen".
   * Die Änderung geht mit `ersetzen = true` erneut hinaus.
   */
  async qrVorhandenenErsetzen(konfliktId: string): Promise<void> {
    const db = this.db.db;
    const konflikt = await db.konflikte.get(konfliktId);

    if (!konflikt || konflikt.art !== 'QR' || !konflikt.qr?.ersetzenMoeglich) {
      return;
    }

    const erneut: SyncQueueEintrag = {
      ...konflikt.aenderung,
      payload: { ...(konflikt.aenderung.payload ?? {}), ersetzen: true },
      baseVersion: null,
      retryCount: 0,
      letzterFehler: null,
      endgueltigFehlgeschlagen: false
    };

    await db.transaction('rw', db.konflikte, db.syncQueue, db.qrZuordnungen, async () => {
      if (konflikt.messstelleId != null) {
        const lokal = await db.qrZuordnungen.get(konflikt.messstelleId);

        if (lokal && !lokal.vorherigerToken && konflikt.qr?.vorhandenerToken) {
          await db.qrZuordnungen.put({ ...lokal, vorherigerToken: konflikt.qr.vorhandenerToken });
        }
      }

      await db.syncQueue.put(erneut);
      await db.konflikte.delete(konfliktId);
    });

    await this.aktualisiereStatus();
    await this.synchronisiere('manuell');
  }

  /** Alle offenen Konflikte, für die Anzeige. */
  async ladeKonflikte(listeId?: number): Promise<SyncKonfliktEintrag[]> {
    return this.db.ladeKonflikte(listeId);
  }

  /**
   * Änderungen, die der Server endgültig abgelehnt hat.
   *
   * Sie bleiben sichtbar liegen, statt still zu verschwinden: der Benutzer
   * soll erfahren, was NICHT angekommen ist – und es gegebenenfalls von Hand
   * nachtragen.
   */
  async ladeFehlgeschlagene(listeId?: number): Promise<SyncQueueEintrag[]> {
    const alle = await this.db.ladeWarteschlange(listeId);

    return alle.filter(e => e.endgueltigFehlgeschlagen === true);
  }

  /**
   * Verwirft endgültig abgelehnte Änderungen. Ausdrücklich vom Benutzer
   * angestoßen – automatisch verschwindet hier nichts.
   */
  async verwerfeFehlgeschlagene(): Promise<number> {
    const db = this.db.db;

    const betroffen = await db.syncQueue
      .filter(e => e.endgueltigFehlgeschlagen === true)
      .toArray();

    await db.syncQueue.bulkDelete(betroffen.map(e => e.id));
    await this.aktualisiereStatus();

    return betroffen.length;
  }

  /**
   * Stellt endgültig abgelehnte Änderungen erneut in die Warteschlange –
   * etwa nachdem die Berechtigung wiederhergestellt wurde. Der Benutzer
   * entscheidet, nicht die Anwendung.
   */
  async wiederholeFehlgeschlagene(): Promise<number> {
    const db = this.db.db;

    const betroffen = await db.syncQueue
      .filter(e => e.endgueltigFehlgeschlagen === true)
      .toArray();

    await db.transaction('rw', db.syncQueue, async () => {
      for (const e of betroffen) {
        await db.syncQueue.update(e.id, {
          endgueltigFehlgeschlagen: false,
          retryCount: 0,
          letzterFehler: null
        });
      }
    });

    await this.aktualisiereStatus();
    await this.synchronisiere('manuell');

    return betroffen.length;
  }

  // ===================================================================
  // STATUS
  // ===================================================================

  /** Änderungen, die noch einen Versuch wert sind. */
  private async offeneEintraege(): Promise<SyncQueueEintrag[]> {
    const alle = await this.db.ladeWarteschlange();

    return alle.filter(e => !e.endgueltigFehlgeschlagen);
  }

  async aktualisiereStatus(): Promise<SyncStatus> {
    if (!(await this.db.istVerfuegbar())) {
      return this.status$.value;
    }

    const alle = await this.db.ladeWarteschlange();
    const konflikte = await this.db.zaehleKonflikte();

    const abgelehnt = alle.filter(e => e.endgueltigFehlgeschlagen);
    const fehlgeschlagen = abgelehnt.length;
    const ausstehend = alle.length - fehlgeschlagen;

    const letzterSync = await this.letzterSyncStand();

    const status: SyncStatus = {
      zustand: this.ermittleZustand(ausstehend, konflikte, fehlgeschlagen),
      ausstehend,
      konflikte,
      fehlgeschlagen,
      letzterSyncUtc: letzterSync,
      letzterFehlertext: abgelehnt[0]?.letzterFehler ?? null,
      meldung: ''
    };

    status.meldung = this.meldungZu(status);

    this.status$.next(status);

    return status;
  }

  private ermittleZustand(
    ausstehend: number,
    konflikte: number,
    fehlgeschlagen: number
  ): SyncStatus['zustand'] {
    if (this.laeuft) {
      return 'laeuft';
    }

    // Konflikte zuerst: sie brauchen eine Entscheidung und sind damit
    // dringender als alles andere.
    if (konflikte > 0) {
      return 'konflikt';
    }

    if (fehlgeschlagen > 0) {
      return 'fehler';
    }

    if (!this.network.browserMeldetOnline || !this.network.backendGiltAlsErreichbar) {
      return 'offline';
    }

    if (ausstehend > 0) {
      return 'ausstehend';
    }

    return 'online';
  }

  private meldungZu(status: SyncStatus): string {
    switch (status.zustand) {
      case 'laeuft':
        return 'Synchronisierung läuft …';

      case 'konflikt':
        return status.konflikte === 1
          ? '1 Konflikt braucht eine Entscheidung'
          : `${status.konflikte} Konflikte brauchen eine Entscheidung`;

      case 'fehler':
        return status.fehlgeschlagen === 1
          ? '1 Änderung konnte nicht synchronisiert werden'
          : `${status.fehlgeschlagen} Änderungen konnten nicht synchronisiert werden`;

      case 'offline':
        return status.ausstehend > 0
          ? `Offline · ${status.ausstehend} ${status.ausstehend === 1 ? 'Änderung' : 'Änderungen'} ausstehend`
          : 'Offline';

      case 'ausstehend':
        return `${status.ausstehend} ${status.ausstehend === 1 ? 'Änderung' : 'Änderungen'} ausstehend`;

      default:
        return 'Online · Alles synchronisiert';
    }
  }

  private async letzterSyncStand(): Promise<string | null> {
    const listen = await this.db.ladeListen();
    const projekte = await this.db.ladeProjekte();

    const staende = [...listen, ...projekte]
      .map(l => l.lastSyncAtUtc)
      .filter((s): s is string => !!s)
      .sort();

    return staende.length > 0 ? staende[staende.length - 1] : null;
  }

  private setzeStatus(teil: Partial<SyncStatus>): void {
    this.status$.next({ ...this.status$.value, ...teil });
  }

  /**
   * Hilfsmittel für die Aufrufer: gehört diese Kennung zu einem noch nicht
   * synchronisierten Datensatz?
   */
  static istLokal(id: unknown): boolean {
    return istLokaleId(id as any);
  }
}
